import React from "react";
import { useGarmentMeasurementServiceContext } from "../../../contexts/GarmentMeasurementServiceContext";
import { processImageForDisplay } from "../../../utilities/helper/processImageForDisplay";
import { CloseCircle, Refresh, TickCircle } from "iconsax-react";

interface GarmentMeasurementServiceImageListProps {
  showMeasurements?: boolean;
  className?: string;
}

const GarmentMeasurementServiceImageList: React.FC<GarmentMeasurementServiceImageListProps> = ({ showMeasurements = false, className }) => {
  const { uploadedImages, currentImageIndex, setCurrentImageIndex, updateMeasurementForCurrentImage, processingStatus, measurementErrors } = useGarmentMeasurementServiceContext();

  const validImages = uploadedImages.filter((img) => img !== "");

  if (validImages.length === 0) {
    return (
      <div className="flex items-center justify-center p-4 my-4 border rounded-lg border-gorgonzola-blue-100">
        <p className="text-gorgonzola-blue-300">No images uploaded</p>
      </div>
    );
  }

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    updateMeasurementForCurrentImage();
  };

  const getStatusIcon = (index: number) => {
    const status = processingStatus[index];

    if (status === "processing") {
      return (
        <div className="absolute flex items-center justify-center bg-white border rounded-full border-gorgonzola-blue-100 size-6 top-2 right-2">
          <Refresh className="text-gorgonzola-blue-400 size-5 animate-spin" />
        </div>
      );
    } else if (status === "success") {
      return (
        <div className="absolute flex items-center justify-center bg-white border rounded-full border-gorgonzola-blue-100 size-6 top-2 right-2">
          <TickCircle className="text-emerald-500 size-5" variant="Bold" />
        </div>
      );
    } else if (status === "error") {
      return (
        <div className="absolute flex items-center justify-center bg-white border rounded-full border-gorgonzola-blue-100 size-6 top-2 right-2">
          <CloseCircle className="text-english-vermillion-500 size-5" variant="Bold" />
        </div>
      );
    } else if (status === "pending") {
      return (
        <div className="absolute flex items-center justify-center px-2 py-1 text-[13px] leading-3 border bg-white border-gorgonzola-blue-100 rounded-full top-2 right-2">
          <span className="text-gorgonzola-blue-400">Pending</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 place-items-center gap-6 w-full ${className || ""}`}>
      {validImages.map((image, index) => {
        const imageBase64 = processImageForDisplay(image);
        const isSelected = index === currentImageIndex;
        const hasError = !!measurementErrors[index];

        return (
          <div
            key={index}
            className={`relative cursor-pointer transition-all border rounded-lg max-w-[15rem] overflow-hidden ${isSelected ? "ring-2 ring-shindig-500 scale-[1.02]" : "border-gorgonzola-blue-100"}`}
            onClick={() => handleImageClick(index)}
          >
            <div className="relative grid place-content-center w-full h-[20rem]">
              <img src={`data:image/png;base64,${imageBase64}`} alt={`Uploaded garment ${index + 1}`} className="object-contain w-full h-full" />
            </div>
            <div className="absolute grid text-[13px] leading-3 rounded-full size-6 place-content-center top-2 left-2 border bg-white border-gorgonzola-blue-100 text-gorgonzola-blue-600">{index + 1}</div>
            {getStatusIcon(index)}

            {hasError && processingStatus[index] === "error" && <div className="absolute bottom-0 left-0 right-0 p-2 text-xs text-white bg-english-vermillion-500">{measurementErrors[index]}</div>}

            {hasError && processingStatus[index] === "success" && <div className="absolute bottom-0 left-0 right-0 p-2 text-white text-2xs bg-english-vermillion-500">{measurementErrors[index]}</div>}
          </div>
        );
      })}
    </div>
  );
};

export default GarmentMeasurementServiceImageList;
